// =============================================================
// BatchCalendar
// -------------------------------------------------------------
// Read-only view of "what is the faculty teaching, to which
// batch, at what time, and how far through the syllabus
// are they?" — reuses the existing
//   - TenantClassSchedule (timetable allocation)
//   - TenantCourse.courseColor (brand colour)
//   - TenantCourseTopicProgress (progress %)
// and is read-only. No new progress system, no new data model.
//
//   Admin / manager: pick a faculty → see every batch.
//   Faculty: see only their own batches.
//
// One row per schedule (course + batch + time slot + progress).
// Rows are grouped by faculty on the admin view; faculty view
// just lists "My Batches" in start-time order.
// =============================================================

import React, { useEffect, useState, useCallback, useMemo } from 'react';
import api from '../../services/api';
import { useAuth } from '../../context/AuthContext';

// ─── Global keyframes for the progress animations ──────────────────────────
//
// We inject these via a one-time <style> tag rather than relying on
// a CSS file so the component is self-contained and easy to drop
// anywhere. Animations are kept subtle — the goal is "alive", not
// "distracting". The shimmer on a 100% bar is the only one that
// really moves; everything else is a soft scale-pulse.
const ANIMATION_CSS = `
@keyframes bc-pulse {
  0%, 100% { transform: scaleY(1);   opacity: 1; }
  50%      { transform: scaleY(1.4); opacity: 0.85; }
}
@keyframes bc-fill-in {
  0%   { transform: scaleX(0); opacity: 0; }
  100% { transform: scaleX(1); opacity: 1; }
}
@keyframes bc-shimmer {
  0%   { transform: translateX(-100%); }
  100% { transform: translateX(200%); }
}
@keyframes bc-celebrate {
  0%, 100% { transform: scale(1)   rotate(0deg);  }
  25%      { transform: scale(1.3) rotate(-15deg); }
  75%      { transform: scale(1.3) rotate(15deg);  }
}
@keyframes tier-pulse {
  0%, 100% { box-shadow: 0 0 0 0   currentColor; transform: scale(1);    }
  50%      { box-shadow: 0 0 0 6px transparent;     transform: scale(1.05); }
}
.tier-pulse {
  animation: tier-pulse 1.8s ease-in-out infinite;
}
`;

// One-time injection of the keyframes. Idempotent: re-mounts during
// HMR don't double-add the tag.
if (typeof document !== 'undefined' && !document.getElementById('bc-animations')) {
  const tag = document.createElement('style');
  tag.id = 'bc-animations';
  tag.textContent = ANIMATION_CSS;
  document.head.appendChild(tag);
}

// ─── Tiny hex-shade helper ──────────────────────────────────────────────────
// Darken / lighten a hex colour. Returns a CSS rgb() string.
// Used for hover accents, gradient endpoints, and ring highlights.
const shade = (hex, amount = 0.85) => {
  if (!hex) return '#3B82F6';
  let h = String(hex).trim().replace('#', '');
  if (h.length === 3) h = h.split('').map(c => c + c).join('');
  if (!/^[0-9a-fA-F]{6}$/.test(h)) return hex;
  const r = parseInt(h.slice(0, 2), 16);
  const g = parseInt(h.slice(2, 4), 16);
  const b = parseInt(h.slice(4, 6), 16);
  const f = (c) => Math.max(0, Math.min(255, Math.round(c * amount)));
  return `rgb(${f(r)}, ${f(g)}, ${f(b)})`;
};

// Pick a status tier from a percentage. Drives the icon, the ring
// colour, and the celebratory effects on completion. Each tier is
// distinct so users can scan a row of cards and immediately know
// which one needs attention.
const tierFor = (pct) => {
  if (pct >= 100) return { key: 'complete',  icon: 'bi-patch-check-fill',  label: 'Complete',   glow: '#22c55e', pulse: false, celebrate: true  };
  if (pct >= 75)  return { key: 'high',      icon: 'bi-rocket-takeoff-fill', label: 'On Track',  glow: '#10b981', pulse: false, celebrate: false };
  if (pct >= 50)  return { key: 'mid',       icon: 'bi-graph-up-arrow',    label: 'In Progress',glow: '#3b82f6', pulse: false, celebrate: false };
  if (pct >= 25)  return { key: 'low',       icon: 'bi-hourglass-split',   label: 'Getting Started', glow: '#f59e0b', pulse: false, celebrate: false };
  if (pct > 0)    return { key: 'started',   icon: 'bi-play-circle',       label: 'Just Started', glow: '#8b5cf6', pulse: true,  celebrate: false };
  return           { key: 'empty',     icon: 'bi-clipboard-data',     label: 'Not Started',  glow: '#94a3b8', pulse: true,  celebrate: false };
};

// ─── AnimatedProgressBar ──────────────────────────────────────────────────
//
// Renders a horizontally-segmented progress bar. Each segment is
// 10% wide so users can read the exact value at a glance and see
// "how far" visually rather than just a number. The filled segments
// pulse softly (subtle scale animation) so the bar reads as alive
// without being distracting. The track / fill colours track the
// course colour so the same Python bar looks the same everywhere.
//
// Layout:
//   [====    ]  30% · Just Started
//   [=====   ]  50% · In Progress
//   [=======o]  70% · On Track
//   [========o]  80% · On Track
//   [==========] 100% · Complete  ✨
const SEGMENT_COUNT = 10;
const AnimatedProgressBar = ({ percent, color, tier, done, total }) => {
  const safePct = Math.max(0, Math.min(100, Math.round(percent || 0)));
  const filled  = Math.round((safePct / 100) * SEGMENT_COUNT);
  const light   = shade(color, 1.15);
  const dark    = shade(color, 0.75);

  // Debug log — once per render so we can see exactly what values
  // are flowing through. Browsers will only show this once unless
  // React re-renders (which it does on every state change).
  // (Dev-only diagnostic log removed for production cleanliness.)

  return (
    <div>
      {/* Header row: tier icon + label on the left, big % on the right */}
      <div className="d-flex align-items-center justify-content-between mb-2">
        <div className="d-flex align-items-center gap-2">
          <div
            className={`d-flex align-items-center justify-content-center ${tier.pulse ? 'tier-pulse' : ''}`}
            style={{
              width: 26, height: 26, borderRadius: '50%',
              background: tier.glow + '22',
              border: `1.5px solid ${tier.glow}`,
              color: tier.glow, fontSize: 14,
              boxShadow: tier.pulse ? `0 0 0 0 ${tier.glow}55` : 'none',
              transition: 'all .3s ease',
            }}
          >
            <i className={`bi ${tier.icon}`} />
          </div>
          <div>
            <div style={{ fontSize: 10, color: '#64748b', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.4 }}>
              {tier.label}
            </div>
            <div style={{ fontSize: 10, color: '#94a3b8' }}>
              {safePct < 100 ? `${100 - safePct}% to go` : 'All topics done'}
            </div>
          </div>
        </div>
        <div
          className="d-flex align-items-baseline gap-1"
          style={{
            fontFamily: '"Inter", system-ui, sans-serif',
            color: tier.celebrate ? tier.glow : dark,
            transition: 'color .4s ease',
          }}
        >
          <span style={{ fontSize: 22, fontWeight: 800, lineHeight: 1 }}>{safePct}</span>
          <span style={{ fontSize: 12, fontWeight: 700, color: '#64748b' }}>%</span>
          {tier.celebrate && (
            <span style={{ marginLeft: 4, fontSize: 16, animation: 'bc-celebrate 1.2s ease-in-out infinite' }}>✨</span>
          )}
        </div>
      </div>

      {/* Segmented bar: 10 segments, each 10% wide. Filled ones use a
          gradient + subtle scale pulse. Empty ones are dotted. */}
      <div
        className="d-flex gap-1"
        role="progressbar"
        aria-valuenow={safePct} aria-valuemin={0} aria-valuemax={100}
        style={{ height: 14, width: '100%' }}
      >
        {Array.from({ length: SEGMENT_COUNT }).map((_, i) => {
          const isFilled = i < filled;
          const isLastFilled = i === filled - 1;
          return (
            <div
              key={i}
              style={{
                flex: '1 1 0',
                minWidth: 0,
                height: '100%',
                borderRadius: 3,
                // Solid colour base + gradient overlay. We set
                // backgroundColor explicitly so the bar is visible
                // even if the gradient doesn't render (some browsers
                // drop background-image when set alongside background).
                backgroundColor: isFilled ? color : '#e2e8f0',
                backgroundImage: isFilled
                  ? `linear-gradient(135deg, ${shade(color, 1.25)}, ${color} 50%, ${dark})`
                  : 'repeating-linear-gradient(45deg, #e2e8f0, #e2e8f0 3px, #f1f5f9 3px, #f1f5f9 6px)',
                border: isFilled ? `1px solid ${dark}` : '1px solid #cbd5e1',
                position: 'relative',
                overflow: 'hidden',
              }}
            >
              {isFilled && tier.celebrate && i === filled - 1 && (
                <div style={{
                  position: 'absolute', inset: 0,
                  background: `linear-gradient(90deg, transparent, ${light}aa, transparent)`,
                  animation: 'bc-shimmer 2.4s linear infinite',
                }} />
              )}
            </div>
          );
        })}
      </div>

      {/* Topic counter row */}
      <div className="d-flex align-items-center justify-content-between mt-1" style={{ fontSize: 10, color: '#94a3b8' }}>
        <span><i className="bi bi-list-check me-1" />{done || 0} of {total || 0} topics</span>
        {safePct < 100 && (total || 0) > 0 && (
          <span><i className="bi bi-arrow-right-circle me-1" />{Math.max(0, (total || 0) - (done || 0))} remaining</span>
        )}
      </div>
    </div>
  );
};

// Counter row showing the topic count + status pill. Tiny helper so
// we can compose the segmented bar + counter into a single card slot
// without duplicating the props threading.
const ProgressBar = (props) => <AnimatedProgressBar {...props} tier={tierFor(props.percent)} />;

// One batch card — used by both the admin picker and the staff view.
const BatchCard = ({ row, navigate, balanceBadge }) => {
  const color   = row.courseColor || '#3B82F6';
  const accent  = shade(color, 0.92);
  const light   = shade(color, 1.2);
  const tier    = tierFor(row.progress?.percent);
  const onClick = () => {
    // The frontend already has a /student-course page for enrolled
    // students; staff don't have a "view batch detail" route yet
    // (would be a future enhancement), so we just open the course
    // record page so faculty can drill in.
    if (row.courseId) navigate?.(`/course-records?courseId=${row.courseId}&batchId=${row.batchId || ''}`);
  };
  // Status pill metadata — drives the small icon next to the percent
  const statusPill = (() => {
    if (tier.celebrate) return { bg: '#22c55e', fg: '#fff', icon: 'bi-check-circle-fill', label: 'Complete' };
    if (tier.key === 'high') return { bg: light,   fg: '#065f46', icon: 'bi-stars',               label: 'Almost there' };
    if (tier.key === 'mid')  return { bg: light,   fg: '#1e3a8a', icon: 'bi-graph-up',             label: 'In progress' };
    if (tier.key === 'low')  return { bg: '#fef3c7',fg: '#92400e', icon: 'bi-fire',                  label: 'Pick up the pace' };
    if (tier.key === 'started') return { bg: '#f3e8ff', fg: '#5b21b6', icon: 'bi-rocket-takeoff', label: 'Just started' };
    return                     { bg: '#f1f5f9',fg: '#475569', icon: 'bi-clipboard-data',     label: 'Awaiting start' };
  })();

  return (
    <div
      className="card h-100 shadow-sm"
      style={{
        borderRadius: 12,
        // Heavier 2px border in the course's brand color so the
        // course identity is obvious at a glance, not just from a
        // subtle accent stripe.
        border: `2px solid ${shade(color, 0.78)}`,
        cursor: onClick ? 'pointer' : 'default',
        transition: 'transform .14s ease, box-shadow .14s ease',
        // Tinted background gradient so the course's colour is
        // visible on the whole card, not just on a small accent.
        background: tier.celebrate
          ? `linear-gradient(135deg, #fff 0%, ${shade(color, 1.2)}55 100%)`
          : `linear-gradient(180deg, ${shade(color, 1.18)} 0%, #fff 70px, #fff 100%)`,
        position: 'relative',
        overflow: 'hidden',
        boxShadow: `0 1px 3px ${shade(color, 0.5)}30`,
      }}
      onClick={onClick}
      onMouseEnter={e => {
        e.currentTarget.style.transform = 'translateY(-2px)';
        e.currentTarget.style.boxShadow = `0 10px 24px ${shade(color, 0.5)}50`;
      }}
      onMouseLeave={e => { e.currentTarget.style.transform = ''; e.currentTarget.style.boxShadow = `0 1px 3px ${shade(color, 0.5)}30`; }}
    >
      {/* Accent stripe down the left edge — the course's brand colour.
          6px wide (was 4) so it reads as a "tag" even at a glance. */}
      <div style={{
        position: 'absolute', top: 0, left: 0, bottom: 0, width: 6,
        background: `linear-gradient(180deg, ${color} 0%, ${accent} 100%)`,
      }} />

      {/* Balance/end-date badge — rendered at the top-right INSIDE the card so it
          can never be clipped by parent overflow rules. Appears as a colored
          pill that sits flush with the right edge above the header. */}
      {balanceBadge}

      {/* Header: course color avatar + name + status pill */}
      <div className="card-body p-3" style={{ paddingLeft: 16 }}>
        <div className="d-flex align-items-start gap-2 mb-2">
          {/* Square avatar with course initial. Tinted by course color. */}
          <div
            className="d-flex align-items-center justify-content-center flex-shrink-0"
            style={{
              width: 38, height: 38, borderRadius: 9,
              background: `linear-gradient(135deg, ${color} 0%, ${accent} 100%)`,
              color: '#fff', fontWeight: 800, fontSize: 16,
              boxShadow: `0 2px 6px ${shade(color, 0.6)}50`,
            }}
            title={row.courseName}
          >
            {(row.courseName || '?').charAt(0).toUpperCase()}
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div className="d-flex align-items-center gap-1">
              <div className="fw-bold" style={{ fontSize: 15, color: shade(color, 0.5), letterSpacing: -0.2 }}>{row.courseName}</div>
              {row.courseCode && <code style={{ fontSize: 9, color: '#64748b', background: '#f1f5f9', padding: '1px 5px', borderRadius: 3 }}>{row.courseCode}</code>}
            </div>
            <div style={{ fontSize: 11, color: '#64748b', marginTop: 1 }}>
              <i className="bi bi-people-fill me-1" style={{ color }} />
              {row.batchName}
            </div>
          </div>
          {/* Status pill (top right) */}
          <span
            className="badge d-inline-flex align-items-center gap-1"
            style={{
              background: statusPill.bg, color: statusPill.fg,
              fontSize: 9, fontWeight: 700, textTransform: 'uppercase',
              letterSpacing: 0.4, padding: '4px 8px', borderRadius: 999,
              whiteSpace: 'nowrap',
            }}
          >
            <i className={`bi ${statusPill.icon}`} style={{ fontSize: 10 }} />
            {statusPill.label}
          </span>
        </div>

        {/* Classroom only (time pill removed — admin already knows when
            the class is from the Timetable tab; the Batch Card needs
            breathing room, not a duplicate chip). */}
        {row.classroom && (
          <div className="d-flex align-items-center gap-2 mb-2 flex-wrap">
            <span
              className="d-inline-flex align-items-center gap-1"
              style={{ fontSize: 11, color: '#334155', background: '#f8fafc', border: '1px solid #e2e8f0', padding: '2px 8px', borderRadius: 6 }}
            >
              <i className="bi bi-door-open-fill" style={{ color, fontSize: 10 }} />
              Room {row.classroom}
            </span>
          </div>
        )}

        {/* Faculty (only shown for admin view — faculty view shows the
            user's own batches and the faculty field would be redundant) */}
        {row.batchFaculty && (
          <div
            className="d-flex align-items-center gap-1 mb-2"
            style={{ fontSize: 11, color: '#475569' }}
          >
            <i className="bi bi-person-circle" style={{ color, fontSize: 12 }} />
            <span className="text-truncate">{row.batchFaculty}</span>
          </div>
        )}

        {/* Progress — the rich, animated version with segmented bar
            and tier-driven icons. Also surfaces a friendly note when
            the syllabus hasn't been set up yet (`no_topics` scope)
            so the admin knows to add modules/topics in Course Records
            before progress can show up. */}
        <ProgressBar
          percent={row.progress?.percent}
          done={row.progress?.done}
          total={row.progress?.total}
          color={color}
        />
        {row.progress?.scope === 'no_topics' && (
          <div className="mt-2 d-flex align-items-start gap-1" style={{
            fontSize: 11, color: '#92400e',
            background: '#fef3c7', border: '1px solid #fde68a',
            borderRadius: 6, padding: '6px 8px',
          }}>
            <i className="bi bi-info-circle-fill" style={{ color: '#f59e0b', marginTop: 1 }} />
            <span>
              Syllabus not set up yet. Add modules and topics in
              <a href="/course-records" style={{ marginLeft: 4 }}>Course Records</a>
              to start tracking progress.
            </span>
          </div>
        )}
        {(row.progress?.scope === 'course' || row.progress?.scope === 'faculty') && row.progress?.percent > 0 && row.progress?.scope !== 'faculty_batch' && (
          <div className="mt-1" style={{ fontSize: 10, color: '#94a3b8', fontStyle: 'italic' }}>
            <i className="bi bi-info-circle me-1" />
            {row.progress?.scope === 'course'
              ? 'Showing course-wide progress (no progress recorded for this specific faculty yet).'
              : 'Showing this faculty\'s progress across all batches for this course.'}
          </div>
        )}

        {/* Topic note if present */}
        {row.topic && (
          <div
            className="mt-2 pt-2 d-flex align-items-start gap-1"
            style={{
              fontSize: 11, color: '#475569',
              borderTop: '1px dashed #e2e8f0',
            }}
          >
            <i className="bi bi-bookmark-star-fill" style={{ color, fontSize: 10, marginTop: 2 }} />
            <span className="text-truncate">{row.topic}</span>
          </div>
        )}
      </div>
    </div>
  );
};

export default function BatchCalendar({ navigate, onSwitchTab }) {
  const { user } = useAuth();
  const [loading, setLoading]     = useState(false);
  const [faculty, setFaculty]     = useState(null);     // selected faculty
  const [batches, setBatches]     = useState([]);       // rows
  const [faculties, setFaculties] = useState([]);       // dropdown options
  const [pickerId, setPickerId]   = useState('');       // selected faculty id (admin)
  const [error, setError]         = useState(null);

  // useAuth already gives us the role (set by /auth/me on app load).
  // No need to re-fetch — avoids a race where the role is null for a
  // frame after mount and the dropdown silently doesn't load.
  const userRole = user?.roleKey || user?.role || '';
  const isAdmin = ['admin', 'manager', 'team_lead', 'super_admin'].includes(userRole);

  // View mode for admins:
  //   'all'     — show every batch in the tenant (DEFAULT)
  //   'faculty' — show only the picked faculty's batches
  // The dropdown includes a special "All Batches" option at the top
  // that switches back to 'all'.
  const [viewMode, setViewMode] = useState(isAdmin ? 'all' : 'faculty');
  const [activeTab, setActiveTab] = useState('active'); // 'active' | 'completed'

  // Load the dropdown options only when admin. The default view
  // for admins is "All Batches" so they see the full picture; the
  // dropdown lets them narrow down to one faculty. Faculty always
  // sees only their own batches (no dropdown, no /all endpoint).
  useEffect(() => {
    if (!userRole) return;     // wait until AuthContext has hydrated
    let cancelled = false;
    const init = async () => {
      try {
        if (isAdmin) {
          const opt = await api.get('/training/batch-calendar/options');
          if (cancelled) return;
          setFaculties(opt.data?.data?.faculties || []);
          // Default: show all batches. The picker stays empty so
          // the dropdown renders the "All Batches" placeholder.
          setPickerId('');
          await loadAll();
        } else {
          // Faculty: hit /my directly. Use the auth user id so the
          // "filter by faculty" picker correctly shows the logged-in
          // user's own name when they revisit the page.
          setPickerId(user?.id || user?._id || '');
          await loadMy();
        }
      } catch (e) {
        if (cancelled) return;
        setError('Could not load batch calendar.');
      }
    };
    init();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userRole]);

  const loadAll = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const r = await api.get('/training/batch-calendar/all');
      const fetchedBatches = r.data?.data?.batches || [];
      setFaculty(r.data?.data?.faculty || null);
      setBatches(fetchedBatches);
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error('[BatchCalendar] /all failed:', e);
      setError('Could not load all batches.');
    }
    setLoading(false);
  }, []);

  const loadMy = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const r = await api.get('/training/batch-calendar/my');
      setFaculty(r.data?.data?.faculty || null);
      setBatches(r.data?.data?.batches || []);
    } catch (e) {
      setError('Could not load your batch calendar.');
    }
    setLoading(false);
  }, []);

  const loadFor = useCallback(async (facultyId) => {
    if (!facultyId) return;
    setLoading(true);
    setError(null);
    try {
      const r = await api.get('/training/batch-calendar', { params: { facultyId } });
      setFaculty(r.data?.data?.faculty || null);
      setBatches(r.data?.data?.batches || []);
    } catch (e) {
      setError('Could not load batch calendar for the selected faculty.');
    }
    setLoading(false);
  }, []);

  // Compute balance days for a batch row (days remaining until end date)
  const computeBalanceDays = (row) => {
    if (!row.batchEndDate) return null;
    const end = new Date(row.batchEndDate);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    end.setHours(0, 0, 0, 0);
    const diff = Math.round((end - today) / (1000 * 60 * 60 * 24));
    return diff;
  };

  // Format date as dd MMM yyyy (e.g., "26 Jul 2026")
  const fmtBatchDate = (d) => {
    if (!d) return '';
    return new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
  };

  // Group rows by batch so multiple slots for the same batch collapse
  // into a single card with all of its slots listed. This matches the
  // user's spec: "Python Morning Batch" is the unit, not "slot 1".
  const grouped = (() => {
    const map = new Map();
    for (const r of batches) {
      const k = `${r.courseId}::${r.batchId || 'unassigned'}`;
      if (!map.has(k)) {
        map.set(k, { ...r, slots: [] });
      }
      map.get(k).slots.push(r);
    }
    return [...map.values()];
  })();

  // Split groups into active vs completed based on batch status
  const activeGroups = grouped.filter(g => g.batchStatus !== 'completed');
  const completedGroups = grouped.filter(g => g.batchStatus === 'completed');
  const visibleGroups = activeTab === 'completed' ? completedGroups : activeGroups;

  if (error) {
    return (
      <div className="alert alert-danger">
        <i className="bi bi-exclamation-triangle me-2" />
        {error}
      </div>
    );
  }

  if (loading && !batches.length) {
    return (
      <div className="text-center py-5">
        <div className="spinner-border text-primary" />
        <div className="text-muted mt-2">Loading batch calendar…</div>
      </div>
    );
  }

  return (
    <div>
      {/* Header: faculty picker (admin only) or "My Batches" label */}
      {isAdmin ? (
        <div className="card shadow-sm mb-3" style={{ borderRadius: 12 }}>
          <div className="card-body p-3">
            <div className="d-flex align-items-center gap-2 flex-wrap">
              <div style={{ fontWeight: 700, fontSize: 15, color: '#1e293b' }}>
                <i className="bi bi-calendar3 me-2" style={{ color: '#6366f1' }} />
                Batch Calendar
              </div>
              <div className="ms-auto d-flex align-items-center gap-2 flex-wrap">
                <label className="form-label mb-0 small fw-semibold text-muted">
                  {viewMode === 'all' ? 'View' : 'Filter by faculty'}
                </label>
                <select
                  className="form-select form-select-sm"
                  style={{ minWidth: 320, maxWidth: 480 }}
                  value={pickerId}
                  onChange={e => {
                    const v = e.target.value;
                    setPickerId(v);
                    if (!v) {
                      // Empty selection = "All Batches" view
                      setViewMode('all');
                      loadAll();
                    } else {
                      setViewMode('faculty');
                      loadFor(v);
                    }
                  }}
                >
                  <option value="">— All Batches ({faculties.reduce((sum, f) => sum + (f.batchCount || 0), 0)} scheduled) —</option>
                  {faculties.map(f => {
                    // Each faculty option shows their name + email +
                    // assigned batches. We render the batch labels as
                    // a single space-separated string (the option's
                    // textContent is what shows in the collapsed
                    // picker once a value is chosen). The full batch
                    // list is also rendered as a disabled <optgroup>
                    // below the option text so admins see the
                    // detail when they open the dropdown.
                    const batchCount = f.batchCount || 0;
                    const batchLabels = f.batchLabels || [];
                    // Aggregate role keys for display (primary + secondary).
                    const roles = [f.roleKey, ...(f.additionalRoleKeys || [])]
                      .filter(Boolean)
                      .filter((v, i, a) => a.indexOf(v) === i);
                    const roleLabel = roles.length ? ` [${roles.join(', ')}]` : '';
                    return (
                      <option key={f.id} value={f.id}>
                        {f.firstName} {f.lastName}{roleLabel}
                        {batchCount > 0
                          ? ` — ${batchCount} batch${batchCount === 1 ? '' : 'es'}${batchLabels.length ? `: ${batchLabels.join(', ')}` : ''}`
                          : ' — no batches assigned'}
                      </option>
                    );
                  })}
                </select>
                <span className="badge bg-light text-dark border" style={{ fontSize: 10 }}>
                  <i className="bi bi-people-fill me-1" />
                  {faculties.length} {faculties.length === 1 ? 'teaching staff' : 'teaching staff'}
                </span>
              </div>
            </div>
          </div>
        </div>
      ) : (
        <div className="card shadow-sm mb-3" style={{ borderRadius: 12 }}>
          <div className="card-body p-3">
            <div className="d-flex align-items-center gap-2 flex-wrap">
              <div style={{ fontWeight: 700, fontSize: 15, color: '#1e293b' }}>
                <i className="bi bi-calendar3 me-2" style={{ color: '#6366f1' }} />
                My Batch Calendar
              </div>
              {grouped.length > 0 && (
                <div className="ms-auto d-flex gap-2 align-items-center">
                  <span className="badge bg-light text-dark border" style={{ fontSize: 10 }}>
                    <i className="bi bi-collection me-1" />
                    {grouped.length} {grouped.length === 1 ? 'batch' : 'batches'}
                  </span>
                  <span className="badge bg-light text-dark border" style={{ fontSize: 10 }}>
                    <i className="bi bi-clock me-1" />
                    {batches.length} {batches.length === 1 ? 'slot' : 'slots'}
                  </span>
                </div>
              )}
            </div>
            <div className="small text-muted mt-1">
              Your allocated batches and the progress through each syllabus.
            </div>
          </div>
        </div>
      )}

      {/* Selected faculty summary (admin only) — shows name, email,
          batch count, and a per-batch breakdown so the admin can
          see exactly which courses this faculty is teaching
          without opening each card. */}
{isAdmin && (viewMode === 'all' || faculty) && (
        <div className="card shadow-sm mb-3" style={{ borderRadius: 12, border: '1px solid #e2e8f0' }}>
          <div className="card-body p-3">
            <div className="d-flex align-items-center gap-2 flex-wrap">
              <div
                className="d-flex align-items-center justify-content-center flex-shrink-0"
                style={{
                  width: 40, height: 40, borderRadius: '50%',
                  background: viewMode === 'all'
                    ? 'linear-gradient(135deg, #3B82F6 0%, #1D4ED8 100%)'
                    : 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
                  color: '#fff', fontWeight: 800, fontSize: 16,
                  boxShadow: viewMode === 'all'
                    ? '0 2px 6px rgba(59, 130, 246, 0.4)'
                    : '0 2px 6px rgba(99, 102, 241, 0.4)',
                }}
                title={viewMode === 'all' ? 'All batches' : `${faculty.firstName} ${faculty.lastName}`}
              >
                {viewMode === 'all'
                  ? <i className="bi bi-collection" style={{ fontSize: 18 }} />
                  : (faculty.firstName?.[0] || faculty.email?.[0] || '?').toUpperCase()
                }
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div className="fw-bold" style={{ fontSize: 15, color: '#0f172a' }}>
                  {viewMode === 'all' ? 'All Batches' : `${faculty.firstName} ${faculty.lastName}`}
                </div>
                <div className="small text-muted" style={{ marginTop: 1 }}>
                  {faculty && viewMode === 'faculty' && faculty.email && (
                    <span><i className="bi bi-envelope me-1" />{faculty.email}</span>
                  )}
                  {faculty && viewMode === 'faculty' && faculty.email && ' · '}
                  <i className="bi bi-collection me-1" />
                  {grouped.length} {grouped.length === 1 ? 'batch' : 'batches'}
                  {' · '}
                  <i className="bi bi-clock me-1" />
                  {batches.length} scheduled {batches.length === 1 ? 'slot' : 'slots'}
                </div>
              </div>
              {pickerId && faculties.find(f => f.id === pickerId)?.primaryColor && (
                <span
                  title="Primary course color"
                  style={{
                    width: 12, height: 12, borderRadius: '50%',
                    background: faculties.find(f => f.id === pickerId).primaryColor,
                    boxShadow: `0 0 0 2px #fff, 0 0 0 3px ${faculties.find(f => f.id === pickerId).primaryColor}40`,
                  }}
                />
              )}
            </div>
            {pickerId && (() => {
              const f = faculties.find(x => x.id === pickerId);
              if (!f || !f.batchCount) return null;
              return (
                <div className="mt-2 d-flex flex-wrap gap-1">
                  {f.batchLabels.map((label, i) => (
                    <span
                      key={i}
                      className="badge"
                      style={{
                        background: '#f1f5f9',
                        color: '#334155',
                        fontSize: 10,
                        fontWeight: 600,
                        padding: '3px 8px',
                        borderRadius: 999,
                        border: '1px solid #e2e8f0',
                      }}
                    >
                      <i className="bi bi-mortarboard me-1" />
                      {label}
                    </span>
                  ))}
                  {f.batchCount > f.batchLabels.length && (
                    <span
                      className="badge"
                      style={{ background: '#f1f5f9', color: '#64748b', fontSize: 10, padding: '3px 8px' }}
                    >
                      +{f.batchCount - f.batchLabels.length} more
                    </span>
                  )}
                </div>
              );
            })()}
          </div>
        </div>
      )}

      {/* Empty state — actionable so admins know exactly how to
          populate this view: open the Timetable tab and schedule a
          class. Faculty see a friendly note pointing them at the
          admin. We deliberately do NOT auto-create schedules here. */}
      {!loading && batches.length === 0 && (
        <div className="text-center text-muted py-5">
          <i className="bi bi-calendar-x" style={{ fontSize: 40, opacity: 0.3 }} />
            <div className="mt-2 mb-2" style={{ fontSize: 14 }}>
            {isAdmin
              ? (pickerId
                  ? 'No scheduled batches for this faculty yet.'
                  : 'No batches are scheduled in this tenant yet. Switch to the Timetable tab to schedule a class.')
              : 'No batches are scheduled for you yet.'}
          </div>
          {isAdmin && (
            <button type="button" className="btn btn-sm btn-primary"
              onClick={() => onSwitchTab ? onSwitchTab('timetable') : (window.location.hash = '#timetable')}>
              <i className="bi bi-plus-lg me-1" />Schedule a class
            </button>
          )}
          {!isAdmin && (
            <div className="text-muted small">
              Ask your admin to add you to a class schedule on the Timetable page.
            </div>
          )}
        </div>
      )}

      {/* Tabs: Active vs Completed */}
      {isAdmin && grouped.length > 0 && (
        <ul className="nav nav-pills mb-3 gap-2">
          <li className="nav-item">
            <button className={`nav-link d-flex align-items-center gap-2 ${activeTab === 'active' ? 'active' : ''}`}
              style={{ borderRadius: 8, fontWeight: 600 }}
              onClick={() => setActiveTab('active')}>
              <i className="bi bi-calendar-check"></i>
              <span>Active Batches</span>
              <span className={`badge ${activeTab === 'active' ? 'bg-white text-primary' : 'bg-light text-secondary'}`} style={{ fontSize: 11 }}>
                {activeGroups.length}
              </span>
            </button>
          </li>
          <li className="nav-item">
            <button className={`nav-link d-flex align-items-center gap-2 ${activeTab === 'completed' ? 'active' : ''}`}
              style={{ borderRadius: 8, fontWeight: 600 }}
              onClick={() => setActiveTab('completed')}>
              <i className="bi bi-check2-circle"></i>
              <span>Completed Batches</span>
              <span className={`badge ${activeTab === 'completed' ? 'bg-white text-success' : 'bg-light text-secondary'}`} style={{ fontSize: 11 }}>
                {completedGroups.length}
              </span>
            </button>
          </li>
        </ul>
      )}

      {/* Grouped batch cards */}
      <div className="row g-3">
        {visibleGroups.length === 0 ? (
          <div className="col-12 text-center text-muted py-4">
            <i className="bi bi-inbox" style={{ fontSize: 32, opacity: 0.3 }}></i>
            <div className="mt-2">
              {activeTab === 'completed'
                ? 'No completed batches yet.'
                : 'No active batches. All batches are completed.'}
            </div>
          </div>
        ) : visibleGroups.map((g, i) => {
          const allSlots = g.slots.map(s => s.timeSlot).filter(Boolean);
          const balanceDays = computeBalanceDays(g);
          const balanceAlertClass = balanceDays !== null && balanceDays < 0
            ? 'alert-danger'
            : balanceDays !== null && balanceDays <= 7
            ? 'alert-warning'
            : 'alert-info';
          const balanceBadge = (balanceDays !== null || g.batchEndDate) ? (
            <div
              className={`alert ${balanceAlertClass} py-1 px-2 mb-0 d-inline-flex align-items-center gap-1`}
              style={{
                position: 'absolute',
                top: 8,
                right: 8,
                zIndex: 5,
                fontSize: 11,
                fontWeight: 700,
                borderRadius: 999,
                border: '1.5px solid currentColor',
                padding: '2px 9px',
              }}
              title={`End date: ${fmtBatchDate(g.batchEndDate)}`}>
              <i className="bi bi-calendar-event" style={{ fontSize: 10 }}></i>
              <span>
                {balanceDays !== null && balanceDays < 0
                  ? `${Math.abs(balanceDays)}d overdue`
                  : balanceDays !== null && balanceDays <= 7
                  ? `${balanceDays}d left`
                  : balanceDays !== null
                  ? `${balanceDays}d remaining`
                  : fmtBatchDate(g.batchEndDate)}
              </span>
              {g.batchEndDate && (
                <span style={{ opacity: 0.7, fontWeight: 500, marginLeft: 2 }}>
                  · ends {fmtBatchDate(g.batchEndDate)}
                </span>
              )}
            </div>
          ) : null;
          return (
            <div key={`${g.courseId}-${g.batchId || 'unassigned'}-${i}`} className="col-md-6 col-lg-6">
              <div className="position-relative">
                <BatchCard row={g} navigate={navigate} balanceBadge={balanceBadge} />
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
